import type { ChatWorkflowRequestBodyDto } from "@intelligems/ig-types/dtos/ai/workflows/chat-workflow";
import type { UiMessage } from "@intelligems/ig-types/schemas/ai/ui-message";
import type { UiStreamAction } from "@intelligems/ig-types/schemas/ai/ui-stream-actions";
import { WORKFLOW_IDS } from "@intelligems/ig-plugin/utils/workflow-ids";
import { isAbortError } from "@intelligems/ig-utils/stream-utils";

import type { AgenticLogicCreationInitialContext } from "./agenticLogicCreationContext";
import {
  type CompletedAgenticLogicRule,
  createAgenticLogicCreationTracker,
  trackAgenticLogicCreation,
} from "./agenticLogicCreationEvents";
import {
  type AgenticLogicScenarioRun,
  createAgenticLogicScenarioTracker,
  trackAgenticLogicScenarioRun,
} from "./agenticLogicScenarioEvents";
import {
  type AgenticLogicWriteScope,
  createAgenticLogicWriteTracker,
  trackAgenticLogicWrite,
} from "./agenticLogicWriteEvents";
import {
  createInitialAssistantMessage,
  createUserMessage,
} from "./utils/workflowUtils";

/**
 * Atlas's opening line, seeded as the first message of a new session. It is
 * the visible counterpart to the composer placeholder, and it gives the
 * session's persisted transcript an assistant turn to start from rather than
 * opening cold on a user message.
 */
const OPENING_ASSISTANT_TEXT = "Describe what the rule should do.";

export interface AgenticLogicCreationControllerDependencies {
  createId: () => string;
  dispatch: (action: UiStreamAction | UiStreamAction[]) => void;
  mutate: (payload: ChatWorkflowRequestBodyDto) => Promise<void>;
  onComplete: (
    rule: CompletedAgenticLogicRule,
    sessionId: string | null
  ) => void;
  /**
   * The `create_agentic_logic_rule` tool has run: the rule row now exists in
   * the backend while the chat keeps authoring its draft. Fires once per
   * created rule, before the tool output is dispatched into the transcript.
   * Builder-template creations complete outright and go through `onComplete`
   * instead.
   *
   * The session id rides along because it is what associates the rule with the
   * conversation that authored it — the rule row stores it, and the workspace
   * loads that transcript back on a later visit. Null only if the rule somehow
   * arrives before the session was initialized.
   */
  onRuleCreated: (ruleId: string, sessionId: string | null) => void;
  /**
   * A cart run finished. Atlas is the only caller of the evaluate route, so
   * this is how the result reaches the rule workspace's graph canvas.
   */
  onScenarioEvaluated: (run: AgenticLogicScenarioRun) => void;
  /**
   * A tool wrote something the surrounding page is showing. Fires per landed
   * write, for the whole conversation — the rule lifecycle callbacks above
   * fire once each and stop, and an edit or a deploy can happen long after
   * them.
   */
  onWrite: (scopes: AgenticLogicWriteScope[]) => void;
  /**
   * A send failed for a reason other than being stopped. The turn it started
   * may still be running on the server, but nothing here will settle it; the
   * chat marks it interrupted so the composer unlocks and offers Continue.
   */
  onSendFailed: (error: unknown) => void;
  stopChat: (source?: string, sessionId?: string) => void;
  stopStream: () => void;
}

export type AgenticLogicCreationSendContext =
  | {
      initialContext: AgenticLogicCreationInitialContext;
      sessionId?: undefined;
    }
  | {
      initialContext: AgenticLogicCreationInitialContext | null;
      sessionId: string;
    };

const activeControllers = new Set<AgenticLogicCreationController>();

export class AgenticLogicCreationController {
  private active = false;
  private generation = 0;
  private sessionId?: string;
  private tracker = createAgenticLogicCreationTracker();
  private scenarioTracker = createAgenticLogicScenarioTracker();
  private writeTracker = createAgenticLogicWriteTracker();

  constructor(
    private readonly dependencies: AgenticLogicCreationControllerDependencies
  ) {}

  send(
    payload: ChatWorkflowRequestBodyDto,
    context: AgenticLogicCreationSendContext
  ) {
    if (!payload.input) {
      return;
    }

    const inputMessageId = this.dependencies.createId();
    const { message } = createUserMessage(payload.input);
    if (message) {
      message.id = inputMessageId;
    }
    const nodeId = this.dependencies.createId();
    const turnId = this.dependencies.createId();
    // Restored sessions retain their entry on the server. Do not replace it
    // with context reconstructed from a follow-up answer.
    const variables = context.initialContext
      ? { entry: context.initialContext.entry }
      : undefined;
    const generation = ++this.generation;

    let request: ChatWorkflowRequestBodyDto;
    if (!context.sessionId) {
      this.tracker = createAgenticLogicCreationTracker();
      this.scenarioTracker = createAgenticLogicScenarioTracker();
      this.writeTracker = createAgenticLogicWriteTracker();
      this.sessionId = this.dependencies.createId();
      const opener: UiMessage = createInitialAssistantMessage(
        OPENING_ASSISTANT_TEXT
      );
      const actions: UiStreamAction[] = [
        {
          type: "initialize",
          workflowId: WORKFLOW_IDS.AGENTIC_LOGIC_RULE_CREATION,
          sessionId: this.sessionId,
          nodeId,
        },
        {
          type: "addMessage",
          turnId: this.dependencies.createId(),
          message: opener,
          nodeId: this.dependencies.createId(),
          nodeName: "Atlas",
        },
      ];
      if (message) {
        actions.push({
          type: "addMessage",
          turnId,
          message,
          nodeId,
          nodeName: "User Input",
        });
      }
      this.dependencies.dispatch(actions);
      request = {
        workflowId: WORKFLOW_IDS.AGENTIC_LOGIC_RULE_CREATION,
        sessionId: this.sessionId,
        input: payload.input,
        inputMessageId,
        // Seed history with the opener only. The server appends this turn's
        // user message itself (`WorkflowService.dispatchUserInput`), so
        // seeding it here too would put it in `context.messages` twice.
        initialContext: { variables, messages: [opener] },
      };
    } else {
      this.sessionId = payload.sessionId || context.sessionId;
      if (message) {
        this.dependencies.dispatch({
          type: "addMessage",
          turnId,
          message,
          nodeId,
          nodeName: "User Input",
        });
      }
      request = {
        sessionId: this.sessionId,
        input: payload.input,
        inputMessageId,
        ...(variables ? { variables } : {}),
        // The server derives where to re-enter the graph (`resolveResumePoint`):
        // a reply to a parked `request-user-input` node resumes that node in
        // place, a follow-up after a finished turn starts a fresh walk. Forcing
        // a restart here would throw away a parked question mid-answer.
        restartFromFirstNode: false,
      };
    }

    this.dispatchRequest(request, generation);
  }

  /**
   * Re-enters an interrupted turn from what the server last saved, on the same
   * session. A reload cuts the stream the turn was answering on, and nothing
   * reattaches to it; this is the request that picks it up again.
   *
   * It carries no input, so no user message is added — the one the turn was
   * answering is already in the session's history — and no variables, so the
   * turn resumes on the entry the session persisted. The server re-enters the
   * node the session was left on (`resolveResumePoint`: an unsettled session
   * is parked where its last completed node left it).
   */
  resume(sessionId: string) {
    const generation = ++this.generation;
    this.sessionId = sessionId;
    this.dispatchRequest(
      { sessionId, restartFromFirstNode: false },
      generation
    );
  }

  private dispatchRequest(
    request: ChatWorkflowRequestBodyDto,
    generation: number
  ) {
    this.active = true;
    activeControllers.add(this);
    void this.dependencies
      .mutate(request)
      .catch((error: unknown) => {
        this.handleSendFailure(error, generation);
      })
      .finally(() => {
        this.finishGeneration(generation);
      });
  }

  handleStreamAction(action: UiStreamAction) {
    if (!this.active) {
      return;
    }

    // Runs are tracked before the creation lifecycle, and unconditionally: a
    // cart is only ever run against a rule that already exists, so every run
    // lands after `completedRule` has closed the creation tracker.
    const scenarioTracker = trackAgenticLogicScenarioRun(
      this.scenarioTracker,
      action
    );
    const run = scenarioTracker.lastRun;
    if (run && run !== this.scenarioTracker.lastRun) {
      this.dependencies.onScenarioEvaluated(run);
    }
    this.scenarioTracker = scenarioTracker;

    // Writes are tracked unconditionally too, and before the creation
    // lifecycle: an edit, a deploy or a placement change can land at any point
    // in the conversation, including after the tracker below has closed.
    const writeTracker = trackAgenticLogicWrite(this.writeTracker, action);
    const write = writeTracker.lastWrite;
    if (write && write !== this.writeTracker.lastWrite) {
      this.dependencies.onWrite(write.scopes);
    }
    this.writeTracker = writeTracker;

    const tracker = trackAgenticLogicCreation(this.tracker, action);
    const completedRule = this.tracker.completedRule
      ? undefined
      : tracker.completedRule;

    if (
      tracker.createdCodeRuleId &&
      tracker.createdCodeRuleId !== this.tracker.createdCodeRuleId
    ) {
      this.dependencies.onRuleCreated(
        tracker.createdCodeRuleId,
        this.sessionId ?? null
      );
    }

    this.tracker = tracker;
    this.dependencies.dispatch(action);
    if (completedRule) {
      this.dependencies.onComplete(completedRule, this.sessionId ?? null);
    }
  }

  stop(source?: string) {
    if (!this.active) {
      return;
    }
    this.active = false;
    this.generation += 1;
    activeControllers.delete(this);
    this.dependencies.stopChat(source, this.sessionId);
  }

  private handleSendFailure(error: unknown, generation: number) {
    if (generation !== this.generation || !this.active || isAbortError(error)) {
      return;
    }

    this.active = false;
    activeControllers.delete(this);
    this.dependencies.onSendFailed(error);
  }

  private finishGeneration(generation: number) {
    if (generation !== this.generation) {
      return;
    }
    this.active = false;
    activeControllers.delete(this);
  }
}

export const cancelActiveAgenticLogicCreation = (source?: string) => {
  for (const controller of [...activeControllers]) {
    controller.stop(source);
  }
};
